/**
 * MÓDULO DE PRODUTOS MAD (MADEIREIRA)
 * 
 * Este módulo implementa o catálogo de produtos para madeireiras,
 * serrarias e materiais de construção com suporte a cálculos automáticos
 * de preço baseado em metro linear (m), metro quadrado (m²) ou metro cúbico (m³).
 * 
 * CONFORMIDADE:
 * - Multi-tenancy por st_codHashEmpresa
 * - Autenticação e autorização obrigatórias
 * - Cálculos automáticos de preço
 * - Suporte a dimensões, imagens, atributos e tags
 */

import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography, TableSortLabel,
  Tooltip, TablePagination, CircularProgress,
  Tabs, Tab, FormControl, InputLabel, Select, MenuItem,
  Chip, Avatar, DialogContentText, InputAdornment,
  FormControlLabel, Switch
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Build as BuildIcon,
  Calculate as CalculateIcon,
  Image as ImageIcon,
  AttachMoney as AttachMoneyIcon,
  Search as SearchIcon
} from "@mui/icons-material";
import api from "../../services/api";
import env from "../../config/env";
import AcessoRestrito from '../../components/AcessoRestrito';
import { verificarAcessoUsuario } from "../../utils/authStandards";
import {
  prepararDadosParaSalvar,
  tratarErroApi,
  obterCodHashEmpresa
} from "../../utils/crudUtils";
import {
  formatarMoeda,
  formatarMoedaInput,
  removerFormatacao,
  converterMoedaParaNumero
} from "../../utils/formatadores";
import { useSystemMessagesContext } from "../../contexts/SystemMessagesContext";
import { validateForm, VALIDATION_TYPES } from "../../utils/validationUtils";

export default function Prodmad() {
  const location = useLocation();
  const { addSuccess, addError, addWarning, addInfo } = useSystemMessagesContext();

  // Estados principais
  const [produtos, setProdutos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [produtoParaExcluir, setProdutoParaExcluir] = useState(null);
  const [openCalculo, setOpenCalculo] = useState(false);
  const [produtoCalculo, setProdutoCalculo] = useState(null);
  const [quantidadeCalculo, setQuantidadeCalculo] = useState(1);
  const [resultadoCalculo, setResultadoCalculo] = useState(null);

  // Estados para formulário padrão
  const [openForm, setOpenForm] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [produtoSelecionado, setProdutoSelecionado] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [activeTabView, setActiveTabView] = useState(0);

  // Estados para paginação e filtros
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('st_nomeProduto');
  const [filtros, setFiltros] = useState({
    st_nomeProduto: '',
    categoriaId: ''
  });

  // Estados do formulário
  const [formData, setFormData] = useState({
    st_nomeProduto: '',
    st_descricao: '',
    st_sku: '',
    categoriaId: '',
    tipo_item: 'BEM',
    tipo_preco: 'FIXO',
    bo_ativo: 1,
    bo_kit: 0,
    unidades: [],
    dimensoes: [],
    imagens: [],
    atributos: [],
    tags: []
  });
  const [formErrors, setFormErrors] = useState({});
  const [dataHoraAtual, setDataHoraAtual] = useState(new Date());

  // Carregar produtos
  const carregarProdutos = async () => {
    try {
      setLoading(true);

      // Verificar se o token existe antes de fazer a requisição
      const token = localStorage.getItem('token');
      if (!token) {
        addError("Sessão expirada. Por favor, faça login novamente.");
        setProdutos([]);
        setLoading(false);
        return;
      }

      // Timeout de segurança para evitar loading infinito
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout: A requisição demorou muito para responder')), 10000)
      );

      const res = await Promise.race([
        api.get('/prodmad'),
        timeoutPromise
      ]);

      if (Array.isArray(res.data)) {
        setProdutos(res.data);
      } else if (res.data && typeof res.data === 'object') {
        // Se retornar um objeto único, converter para array
        setProdutos([res.data]);
      } else {
        addError("Erro ao carregar produtos: formato de resposta inválido");
        setProdutos([]);
      }
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);

      // Tratar erro de autenticação (401/403)
      if (error.response?.status === 401 || error.response?.status === 403) {
        addError("Sessão expirada. Redirecionando para login...");
        // O interceptor do api.js já vai redirecionar, mas garantimos aqui também
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
        setProdutos([]);
        setLoading(false);
        return;
      }

      // Tratar erro específico de timeout
      if (error.message && error.message.includes('Timeout')) {
        addError("⚠️ O servidor não respondeu à requisição /api/prodmad após 10 segundos. Isso indica um problema no backend: o controller está chamando métodos que não existem no service. Verifique os logs do backend.");
        console.error('❌ Timeout na requisição /api/prodmad');
        console.error('🔍 Problema identificado: ProdmadController.listar() chama ProdmadService.listar(), mas esse método não existe no service.');
        console.error('💡 Solução: Implementar o método listar() no prodmad.service.js ou ajustar o controller para usar os métodos existentes.');
      } else {
        // Mostrar detalhes do erro para debug
        const errorMessage = error.response?.data?.error ||
          error.response?.data?.details ||
          error.message ||
          'Erro desconhecido ao carregar produtos';
        addError(`Erro ao carregar produtos: ${errorMessage}`);
        console.error('Erro detalhado:', error);
      }

      setProdutos([]);
    } finally {
      setLoading(false);
    }
  };

  // Carregar categorias
  const carregarCategorias = async () => {
    try {
      const codHashEmpresa = obterCodHashEmpresa();
      const res = await api.get(`/categoria/empresa/${codHashEmpresa}`);

      if (Array.isArray(res.data)) {
        setCategorias(res.data.filter(cat =>
          cat.st_codHashEmpresa === codHashEmpresa ||
          JSON.parse(localStorage.getItem("usuario") || "{}").perfil === "superusuario"
        ));
      }
    } catch (error) {
      console.error("Erro ao carregar categorias:", error);
    }
  };

  useEffect(() => {
    carregarProdutos();
    carregarCategorias();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setDataHoraAtual(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatarDataHora = (data) => {
    return data.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Funções para confirmação de exclusão
  const confirmarExclusao = (produto) => {
    setProdutoParaExcluir(produto);
    setOpenDeleteConfirm(true);
  };

  const fecharConfirmacaoExclusao = () => {
    setOpenDeleteConfirm(false);
    setProdutoParaExcluir(null);
  };

  const handleInputChange = (event) => {
    const { name, value, type } = event.target;
    let valorFormatado = value;

    setFormData(prev => ({
      ...prev,
      [name]: valorFormatado
    }));

    // Limpa o erro do campo quando ele é alterado
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: false
      }));
    }
  };

  const abrirFormulario = (produto = null) => {
    setProdutoSelecionado(produto);
    const codHashEmpresa = obterCodHashEmpresa();

    if (produto) {
      // Suportar tanto estrutura aninhada (produto.produto) quanto direta (produto)
      const produtoData = produto.produto || produto;

      // Mapear unidades corretamente
      const unidadesMapeadas = (produtoData.unidades || produtoData.prodmad_unidades || []).map(unid => ({
        st_unidade: unid.st_unidade || '',
        vl_preco: unid.vl_preco || '',
        nu_fatorConversao: unid.nu_fatorConversao || ''
      }));

      // Mapear dimensões corretamente
      const dimensoesMapeadas = (produtoData.dimensoes || produtoData.prodmad_dimensaos || []).map(dim => ({
        nu_comprimento: dim.nu_comprimento || '',
        nu_largura: dim.nu_largura || '',
        nu_espessura: dim.nu_espessura || ''
      }));

      // Mapear imagens corretamente
      const imagensMapeadas = (produtoData.imagens || produtoData.prodmad_imagems || []).map(img => ({
        st_urlImagem: img.st_urlImagem || img.st_url || ''
      }));

      // Mapear atributos corretamente
      const atributosMapeados = (produtoData.atributos || produtoData.prodmad_atributos || []).map(attr => ({
        st_nome: attr.st_nomeAtributo || attr.st_nome || '',
        st_valor: attr.st_valorAtributo || attr.st_valor || '',
        st_tipo: attr.st_tipo || 'QUANTIDADE'
      }));

      // Mapear tags corretamente
      const tagsMapeadas = (produtoData.tags || produtoData.prodmad_tags || []).map(tag => ({
        st_tag: tag.st_tag || tag || ''
      }));

      setFormData({
        st_nomeProduto: produtoData.st_nomeProduto || '',
        st_descricao: produtoData.st_descricao || '',
        st_sku: produtoData.st_sku || '',
        categoriaId: produtoData.categoriaId || '',
        tipo_item: produtoData.tipo_item || 'BEM',
        tipo_preco: produtoData.tipo_preco || 'FIXO',
        bo_ativo: produtoData.bo_ativo !== undefined ? produtoData.bo_ativo : 1,
        bo_kit: produtoData.bo_kit !== undefined ? produtoData.bo_kit : 0,
        unidades: unidadesMapeadas,
        dimensoes: dimensoesMapeadas,
        imagens: imagensMapeadas,
        atributos: atributosMapeados,
        tags: tagsMapeadas
      });
    } else {
      setFormData({
        st_nomeProduto: '',
        st_descricao: '',
        st_sku: '',
        categoriaId: '',
        tipo_item: 'BEM',
        tipo_preco: 'FIXO',
        bo_ativo: 1,
        bo_kit: 0,
        unidades: [],
        dimensoes: [],
        imagens: [],
        atributos: [],
        tags: []
      });
    }
    setActiveTab(0);
    setOpenForm(true);
  };

  const validarFormulario = () => {
    const validationRules = {
      st_nomeProduto: [
        { type: VALIDATION_TYPES.REQUIRED },
        { type: VALIDATION_TYPES.MIN_LENGTH, options: { min: 3 } }
      ],
      categoriaId: [
        { type: VALIDATION_TYPES.REQUIRED }
      ]
    };

    const { isValid, errors } = validateForm(formData, validationRules);
    setFormErrors(errors);

    if (!isValid) {
      Object.values(errors).forEach(error => {
        if (error) addWarning(error);
      });
      return false;
    }

    // Validação extra: Unidades
    // Se digitou preço ou fator, tem que ter nome da unidade
    for (let i = 0; i < formData.unidades.length; i++) {
      const u = formData.unidades[i];
      const temConteudo = u.st_unidade?.trim() || u.vl_preco || u.nu_fatorConversao;

      if (temConteudo) {
        if (!u.st_unidade?.trim()) {
          addWarning(`A unidade #${i + 1} precisa de uma descrição/nome.`);
          setActiveTab(1); // Vai para a aba de unidades
          return false;
        }
      }
    }

    // Validação extra: Atributos
    // Se digitou nome ou valor, é recomendável ter os dois, ou pelo menos o Nome
    for (let i = 0; i < formData.atributos.length; i++) {
      const a = formData.atributos[i];
      const temNome = a.st_nome?.trim() || a.st_nomeAtributo?.trim();
      const temValor = a.st_valor?.trim() || a.st_valorAtributo?.trim();

      if (temNome || temValor) {
        if (!temNome) {
          addWarning(`O atributo #${i + 1} precisa de um nome.`);
          setActiveTab(4); // Vai para a aba de atributos
          return false;
        }
        if (!temValor) {
          addWarning(`O atributo "${temNome}" precisa de um valor.`);
          setActiveTab(4);
          return false;
        }
      }
    }

    // Validação extra: Imagens
    // Se abriu campo de imagem, tem que ter URL
    for (let i = 0; i < formData.imagens.length; i++) {
      const img = formData.imagens[i];
      const url = img.st_urlImagem || img.st_url;
      // Se a linha existe, verificamos se está vazia apenas se houver algo digitado parcialmente? 
      // Ignorar imagens vazias.
    }

    return true;
  };

  const salvarProduto = async () => {
    // Validar formulário
    if (!validarFormulario()) {
      addWarning("Por favor, preencha todos os campos obrigatórios corretamente.");
      return;
    }

    try {
      setLoading(true);

      // Preparar dados
      // console.log('📝 FormData Original (Antes da formatação):', formData);
      const dadosFormatados = {
        st_nomeProduto: formData.st_nomeProduto.trim(),
        st_descricao: formData.st_descricao?.trim() || null,
        st_sku: formData.st_sku?.trim() || null,
        categoriaId: parseInt(formData.categoriaId),
        tipo_item: formData.tipo_item || 'BEM',
        tipo_preco: formData.tipo_preco || 'FIXO',
        bo_ativo: formData.bo_ativo !== undefined ? formData.bo_ativo : 1,
        bo_kit: formData.bo_kit !== undefined ? formData.bo_kit : 0,
        unidades: formData.unidades.filter(unid =>
          unid.st_unidade?.trim() ||
          (unid.vl_preco && unid.vl_preco !== '') ||
          (unid.nu_fatorConversao && unid.nu_fatorConversao !== '')
        ).map(unid => ({
          st_unidade: (unid.st_unidade || '').trim(),
          vl_preco: (unid.vl_preco && unid.vl_preco !== '' && !isNaN(parseFloat(unid.vl_preco))) ? parseFloat(unid.vl_preco) : null,
          nu_fatorConversao: (unid.nu_fatorConversao && unid.nu_fatorConversao !== '' && !isNaN(parseFloat(unid.nu_fatorConversao))) ? parseFloat(unid.nu_fatorConversao) : null
        })),
        dimensoes: formData.dimensoes.filter(dim =>
          dim.nu_comprimento || dim.nu_largura || dim.nu_espessura
        ).map(dim => ({
          nu_comprimento: dim.nu_comprimento ? parseFloat(dim.nu_comprimento) : null,
          nu_largura: dim.nu_largura ? parseFloat(dim.nu_largura) : null,
          nu_espessura: dim.nu_espessura ? parseFloat(dim.nu_espessura) : null
        })),
        imagens: formData.imagens.filter(img => img.st_urlImagem?.trim() || img.st_url?.trim()).map(img => ({
          st_urlImagem: (img.st_urlImagem || img.st_url || '').trim()
        })),
        atributos: formData.atributos.filter(attr =>
          (attr.st_nomeAtributo?.trim() || attr.st_nome?.trim()) ||
          (attr.st_valorAtributo?.trim() || attr.st_valor?.trim())
        ).map(attr => ({
          st_nomeAtributo: (attr.st_nomeAtributo || attr.st_nome || '').trim(),
          st_valorAtributo: (attr.st_valorAtributo || attr.st_valor || '').trim(),
          st_tipo: attr.st_tipo || 'QUANTIDADE'
        })),
        tags: formData.tags.filter(tag => tag.st_tag?.trim()).map(tag => ({
          st_tag: tag.st_tag.trim()
        })),
        st_codHashEmpresa: obterCodHashEmpresa()
      };

      // Log para debug
      // Log para debug
      // console.log('🚀 Dados Formatados para Envio (Após formatação):', dadosFormatados);
      // console.log('📤 Unidades detalhadas:', JSON.stringify(dadosFormatados.unidades, null, 2));

      if (produtoSelecionado) {
        const produtoData = produtoSelecionado.produto || produtoSelecionado;
        const produtoId = produtoData.produtoId || produtoData.prodmadId;
        await api.put(`/prodmad/${produtoId}`, dadosFormatados);
        addSuccess("Produto atualizado com sucesso!");
      } else {
        await api.post('/prodmad', dadosFormatados);
        addSuccess("Produto criado com sucesso!");
      }

      setOpenForm(false);
      setFormData({
        st_nomeProduto: '',
        st_descricao: '',
        st_sku: '',
        categoriaId: '',
        tipo_item: 'BEM',
        tipo_preco: 'FIXO',
        bo_ativo: 1,
        bo_kit: 0,
        unidades: [],
        dimensoes: [],
        imagens: [],
        atributos: [],
        tags: []
      });
      setFormErrors({});
      carregarProdutos();
    } catch (error) {
      console.error('❌ Erro ao salvar produto:', error);
      console.error('❌ Detalhes do erro:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      tratarErroApi(error, addError, 'Salvar produto');
    } finally {
      setLoading(false);
    }
  };

  const deletarProduto = async () => {
    if (!produtoParaExcluir) return;

    try {
      setLoading(true);
      const produtoData = produtoParaExcluir.produto || produtoParaExcluir;
      const produtoId = produtoData.produtoId || produtoData.prodmadId;
      await api.delete(`/prodmad/${produtoId}`);
      addSuccess("Produto excluído com sucesso!");
      carregarProdutos();
    } catch (error) {
      tratarErroApi(error, addError, 'Excluir produto');
    } finally {
      setLoading(false);
      fecharConfirmacaoExclusao();
    }
  };

  const calcularPreco = async () => {
    if (!produtoCalculo || !quantidadeCalculo || quantidadeCalculo <= 0) {
      addWarning("Informe uma quantidade válida");
      return;
    }

    try {
      setLoading(true);

      const produto = produtoCalculo.produto || produtoCalculo;
      const produtoId = produto.produtoId || produto.prodmadId;

      if (!produtoId) {
        addError("Produto inválido para cálculo");
        setLoading(false);
        return;
      }

      // Chamar API de cálculo
      const response = await api.get(`/prodmad/${produtoId}/calcular`, {
        params: {
          quantidade: quantidadeCalculo
        }
      });

      const resultado = response.data;

      // Verifica se retornou erro
      if (resultado.mensagem) {
        addWarning(resultado.mensagem);
        setResultadoCalculo({
          tipo: resultado.tipo || '-',
          calculo: resultado.calculo || resultado.metragem || 0,
          preco_unitario: resultado.preco_unitario || 0,
          valor_final: 0,
          mensagem: resultado.mensagem
        });
        return;
      }

      // A API retorna diretamente { tipo, calculo/metragem, preco_unitario, valor_final, quantidade }
      setResultadoCalculo({
        tipo: resultado.tipo,
        calculo: resultado.calculo || resultado.metragem || 0,
        preco_unitario: resultado.preco_unitario || 0,
        valor_final: resultado.valor_final || 0,
        quantidade: resultado.quantidade || quantidadeCalculo,
        mensagem: `Cálculo realizado com sucesso. Tipo: ${resultado.tipo}`
      });

      addSuccess("Cálculo realizado com sucesso!");
    } catch (error) {
      console.error('Erro ao calcular preço:', error);
      tratarErroApi(error, addError, 'Calcular preço');
      setResultadoCalculo(null);
    } finally {
      setLoading(false);
    }
  };

  const abrirCalculo = (produto) => {
    setProdutoCalculo(produto);
    setQuantidadeCalculo(1);
    setResultadoCalculo(null);
    setOpenCalculo(true);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const visualizarProduto = (produto) => {
    setProdutoSelecionado(produto);
    setActiveTabView(0);
    setOpenView(true);
  };

  const fecharFormulario = () => {
    setOpenForm(false);
    setProdutoSelecionado(null);
    setFormErrors({});
  };

  const fecharVisualizacao = () => {
    setOpenView(false);
    setProdutoSelecionado(null);
  };

  const handleFiltroChange = (event) => {
    const { name, value } = event.target;
    setFiltros(prev => ({
      ...prev,
      [name]: value
    }));
    setPage(0);
  };

  // Adicionar dimensão
  const adicionarDimensao = () => {
    setFormData(prev => ({
      ...prev,
      dimensoes: [...prev.dimensoes, {
        nu_comprimento: '',
        nu_largura: '',
        nu_espessura: ''
      }]
    }));
  };

  const removerDimensao = (index) => {
    setFormData(prev => ({
      ...prev,
      dimensoes: prev.dimensoes.filter((_, i) => i !== index)
    }));
  };

  const atualizarDimensao = (index, campo, valor) => {
    setFormData(prev => ({
      ...prev,
      dimensoes: prev.dimensoes.map((dim, i) =>
        i === index ? { ...dim, [campo]: valor } : dim
      )
    }));
  };

  // Adicionar unidade
  const adicionarUnidade = () => {
    setFormData(prev => ({
      ...prev,
      unidades: [...prev.unidades, { st_unidade: '', vl_preco: '', nu_fatorConversao: '' }]
    }));
  };

  const removerUnidade = (index) => {
    setFormData(prev => ({
      ...prev,
      unidades: prev.unidades.filter((_, i) => i !== index)
    }));
  };

  const atualizarUnidade = (index, campo, valor) => {
    setFormData(prev => ({
      ...prev,
      unidades: prev.unidades.map((unid, i) =>
        i === index ? { ...unid, [campo]: valor } : unid
      )
    }));
  };

  // Adicionar imagem
  const adicionarImagem = () => {
    setFormData(prev => ({
      ...prev,
      imagens: [...prev.imagens, { st_urlImagem: '' }]
    }));
  };

  const removerImagem = (index) => {
    setFormData(prev => ({
      ...prev,
      imagens: prev.imagens.filter((_, i) => i !== index)
    }));
  };

  const atualizarImagem = (index, url) => {
    setFormData(prev => ({
      ...prev,
      imagens: prev.imagens.map((img, i) =>
        i === index ? { ...img, st_urlImagem: url } : img
      )
    }));
  };

  // Adicionar atributo
  const adicionarAtributo = () => {
    setFormData(prev => ({
      ...prev,
      atributos: [...prev.atributos, { st_nome: '', st_valor: '', st_tipo: 'QUANTIDADE' }]
    }));
  };

  const removerAtributo = (index) => {
    setFormData(prev => ({
      ...prev,
      atributos: prev.atributos.filter((_, i) => i !== index)
    }));
  };

  const atualizarAtributo = (index, campo, valor) => {
    setFormData(prev => ({
      ...prev,
      atributos: prev.atributos.map((attr, i) =>
        i === index ? { ...attr, [campo]: valor } : attr
      )
    }));
  };

  // Adicionar tag
  const adicionarTag = () => {
    setFormData(prev => ({
      ...prev,
      tags: [...prev.tags, { st_tag: '' }]
    }));
  };

  const removerTag = (index) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index)
    }));
  };

  const atualizarTag = (index, tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.map((t, i) =>
        i === index ? { ...t, st_tag: tag } : t
      )
    }));
  };

  const produtosFiltrados = useMemo(() => {
    return produtos.filter(produto => {
      // Suportar tanto estrutura aninhada (produto.produto) quanto direta (produto)
      const produtoData = produto.produto || produto;
      const nomeMatch = (produtoData.st_nomeProduto || '').toLowerCase()
        .includes(filtros.st_nomeProduto.toLowerCase());
      const categoriaMatch = !filtros.categoriaId ||
        produtoData.categoriaId?.toString() === filtros.categoriaId;
      return nomeMatch && categoriaMatch;
    });
  }, [produtos, filtros]);

  const produtosOrdenados = useMemo(() => {
    return produtosFiltrados.sort((a, b) => {
      const isAsc = order === "asc";
      const produtoA = a.produto || {};
      const produtoB = b.produto || {};

      if (orderBy === "st_nomeProduto") {
        const nomeA = produtoA.st_nomeProduto || "";
        const nomeB = produtoB.st_nomeProduto || "";
        return isAsc ? nomeA.localeCompare(nomeB) : nomeB.localeCompare(nomeA);
      }
      return 0;
    });
  }, [produtosFiltrados, order, orderBy]);

  const produtosPaginados = produtosOrdenados.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Verificar acesso do usuário
  if (!verificarAcessoUsuario("prodmad")) {
    return <AcessoRestrito />;
  }

  return (
    <AcessoRestrito modulo="prodmad">
      <Box
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Box interno com margens de 1,5% */}
        <Box sx={{
          margin: '0 1.5% 1.5% 1.5%',  // 1,5% nas laterais e inferior
          height: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }}>

          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: { xs: 2, sm: 3 },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2
            }}
          >
            <Typography
              variant="h5"
              mb={1}
              sx={{
                fontSize: { xs: '1.5rem', sm: '2rem' },
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <BuildIcon sx={{
                fontSize: { xs: '1.5rem', sm: '2rem' },
                color: '#FFC107' // Mesma cor usada no menu lateral
              }} />
              Produtos MAD
            </Typography>
            <Button
              variant="contained"
              onClick={() => abrirFormulario()}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                minWidth: { xs: 'auto', sm: '200px' },
                backgroundColor: (theme) => theme.palette.mode === 'dark'
                  ? '#ffffff'
                  : theme.palette.primary.main,
                color: (theme) => theme.palette.mode === 'dark'
                  ? '#000000'
                  : theme.palette.primary.contrastText,
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark'
                    ? '#f5f5f5'
                    : theme.palette.primary.dark
                }
              }}
            >
              <AddIcon /> Novo Produto
            </Button>
          </Box>

          {/* Container da Tabela */}
          <TableContainer
            component={Paper}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              overflow: 'auto',
              height: {
                xs: 'calc(90vh - 140px)',  // Mobile: mais espaço para header
                sm: 'calc(90vh - 120px)',  // Tablet: espaço padrão
                md: 'calc(90vh - 120px)',  // Desktop: espaço padrão
                lg: 'calc(90vh - 120px)'   // Large: espaço padrão
              },
              maxHeight: {
                xs: 'calc(90vh - 140px)',
                sm: 'calc(90vh - 120px)',
                md: 'calc(90vh - 120px)',
                lg: 'calc(90vh - 120px)'
              },
              width: '100%', // ✅ 100% da largura da página
              minWidth: { xs: '100%', sm: '600px' }, // ✅ 100% do mínimo também
              maxWidth: '100%', // ✅ Limita a 100% máximo
              boxShadow: (theme) => theme.palette.mode === 'dark'
                ? '0 2px 8px rgba(0, 0, 0, 0.15)'
                : '0 2px 8px rgba(0, 0, 0, 0.06)',
              borderRadius: 2,
              border: 'none'
            }}
          >
            <Table sx={{ minWidth: '100%', tableLayout: 'fixed' }}>
              <TableHead sx={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                backgroundColor: (theme) => theme.palette.background.paper,
                '& .MuiTableCell-root': {
                  backgroundColor: 'inherit',
                  borderBottom: '1px solid rgba(224, 224, 224, 1)'
                }
              }}>
                <TableRow>
                  <TableCell sx={{
                    width: { xs: '25%', sm: '20%' },
                    minWidth: { xs: '150px', sm: '120px' }
                  }}>
                    <TableSortLabel
                      active={orderBy === "st_nomeProduto"}
                      direction={orderBy === "st_nomeProduto" ? order : "asc"}
                      onClick={() => handleRequestSort("st_nomeProduto")}
                    >
                      Nome do Produto
                    </TableSortLabel>
                    <TextField
                      name="st_nomeProduto"
                      size="small"
                      fullWidth
                      value={filtros.st_nomeProduto}
                      onChange={handleFiltroChange}
                      placeholder="Filtrar por nome"
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '0.75rem', sm: '0.875rem' }
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{
                    width: { xs: '20%', sm: '18%' },
                    minWidth: { xs: '120px', sm: '100px' }
                  }}>
                    Categoria
                    <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                      <Select
                        name="categoriaId"
                        value={filtros.categoriaId}
                        onChange={handleFiltroChange}
                        displayEmpty
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' }
                        }}
                      >
                        <MenuItem value="">Todas</MenuItem>
                        {categorias.map((cat) => (
                          <MenuItem key={cat.categoriaId} value={cat.categoriaId}>
                            {cat.st_nomeCategoria}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={{
                    width: { xs: '15%', sm: '12%' },
                    minWidth: { xs: '100px', sm: '80px' }
                  }}>
                    SKU
                  </TableCell>
                  <TableCell sx={{
                    width: { xs: '12%', sm: '10%' },
                    minWidth: { xs: '80px', sm: '70px' }
                  }}>
                    Tipo Item
                  </TableCell>
                  <TableCell sx={{
                    width: { xs: '15%', sm: '12%' },
                    minWidth: { xs: '100px', sm: '80px' }
                  }}>
                    Tipo Preço
                  </TableCell>
                  <TableCell sx={{
                    width: { xs: '15%', sm: '12%' },
                    minWidth: { xs: '100px', sm: '80px' },
                    textAlign: 'center'
                  }}>
                    Ações
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : produtosPaginados.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Nenhum produto encontrado.
                    </TableCell>
                  </TableRow>
                ) : (
                  produtosPaginados.map((item, index) => {
                    const produto = item.produto || item || {};
                    const categoria = categorias.find(c => c.categoriaId === produto.categoriaId);
                    const produtoId = produto.produtoId || produto.prodmadId || `produto-${index}`;

                    return (
                      <TableRow
                        key={produtoId}
                        sx={{
                          backgroundColor: (theme) =>
                            index % 2 === 0
                              ? theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.02)'
                                : 'rgba(0, 0, 0, 0.02)'
                              : 'transparent',
                          '&:hover': {
                            backgroundColor: (theme) =>
                              theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.08)'
                                : 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                      >
                        <TableCell
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            height: '40px', // ✅ Altura fixa para células
                            padding: '8px 16px', // ✅ Padding otimizado para 40px
                            verticalAlign: 'middle' // ✅ Alinhamento vertical centralizado
                          }}
                        >
                          {produto.st_nomeProduto || '-'}
                        </TableCell>
                        <TableCell
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            height: '40px',
                            padding: '8px 16px',
                            verticalAlign: 'middle'
                          }}
                        >
                          {categoria ? categoria.st_nomeCategoria : '-'}
                        </TableCell>
                        <TableCell
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            height: '40px',
                            padding: '8px 16px',
                            verticalAlign: 'middle',
                            fontFamily: 'monospace',
                            fontSize: '0.75rem'
                          }}
                        >
                          {produto.st_sku || '-'}
                        </TableCell>
                        <TableCell
                          sx={{
                            height: '40px',
                            padding: '8px 16px',
                            verticalAlign: 'middle'
                          }}
                        >
                          {produto.tipo_item ? (
                            <Chip
                              label={produto.tipo_item === 'BEM' ? 'Bem' : 'Serviço'}
                              size="small"
                              color={produto.tipo_item === 'BEM' ? 'primary' : 'secondary'}
                              sx={{ fontSize: '0.7rem' }}
                            />
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            height: '40px',
                            padding: '8px 16px',
                            verticalAlign: 'middle'
                          }}
                        >
                          {produto.tipo_preco ? (
                            <Chip
                              label={
                                produto.tipo_preco === 'FIXO' ? 'Fixo' :
                                  produto.tipo_preco === 'SOB_CONSULTA' ? 'Sob Consulta' :
                                    'Variável'
                              }
                              size="small"
                              sx={{
                                backgroundColor: (theme) => theme.palette.mode === 'dark'
                                  ? 'rgba(255, 193, 7, 0.2)'
                                  : 'rgba(255, 193, 7, 0.1)',
                                color: (theme) => theme.palette.mode === 'dark'
                                  ? '#FFC107'
                                  : '#F57C00',
                                fontSize: '0.7rem'
                              }}
                            />
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            height: '40px',
                            padding: '8px 16px',
                            verticalAlign: 'middle',
                            textAlign: 'center'
                          }}
                        >
                          <Tooltip title="Calcular Preço">
                            <IconButton size="small" onClick={() => abrirCalculo(item)}>
                              <CalculateIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Visualizar">
                            <IconButton size="small" onClick={() => visualizarProduto(item)}>
                              <VisibilityIcon fontSize="small" className="material-icon-visibility" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => abrirFormulario(item)}>
                              <EditIcon fontSize="small" className="material-icon-edit" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir">
                            <IconButton size="small" onClick={() => confirmarExclusao(item)}>
                              <DeleteIcon fontSize="small" className="material-icon-delete" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box> {/* Fechamento do Box interno com margens */}

        {/* Paginação fora do Box interno */}
        <TablePagination
          component="div"
          count={produtosFiltrados.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage="Itens por página"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            borderTop: 'none',
            width: '100%',
            marginTop: 0
          }}
        />

        {/* Dialog de Cadastro/Edição */}
        <Dialog
          open={openForm}
          onClose={fecharFormulario}
          maxWidth={false}
          fullWidth={false}
          disableEscapeKeyDown
          disablePortal
          hideBackdrop={false}
          sx={{
            '& .MuiDialog-backdrop': {
              backgroundColor: 'rgba(0, 0, 0, 0.8)'
            }
          }}
          PaperProps={{
            sx: {
              width: { xs: '95vw', sm: '90vw', md: '900px' },
              height: { xs: '95vh', sm: '90vh', md: '600px' },
              maxWidth: { xs: '95vw', sm: '90vw', md: '900px' },
              maxHeight: { xs: '95vh', sm: '90vh', md: '600px' },
              position: 'relative'
            }
          }}
        >
          <DialogTitle sx={{
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderBottom: 1,
            borderColor: 'divider',
            padding: 2,
            position: 'sticky',
            top: 0,
            zIndex: 1,
            fontSize: '1.25rem',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            {produtoSelecionado ? "Editar Produto" : "Novo Produto"}
            <IconButton onClick={fecharFormulario}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{
            height: 'calc(100% - 140px)',
            overflow: 'auto',
            padding: 2,
            '&.MuiDialogContent-root': {
              paddingTop: 2
            },
            '& .MuiInputLabel-root': {
              fontSize: '0.875rem'
            },
            '& .MuiInputBase-input': {
              fontSize: '0.875rem'
            }
          }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
            >
              <Tab label="Dados Básicos" />
              <Tab label="Unidades" />
              <Tab label="Dimensões" />
              <Tab label="Imagens" />
              <Tab label="Atributos" />
              <Tab label="Tags" />
            </Tabs>

            {/* Tab Dados Básicos */}
            {activeTab === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Nome do Produto *"
                  name="st_nomeProduto"
                  value={formData.st_nomeProduto}
                  onChange={handleInputChange}
                  error={!!formErrors.st_nomeProduto}
                  helperText={formErrors.st_nomeProduto}
                  required
                />
                <TextField
                  fullWidth
                  label="Descrição"
                  name="st_descricao"
                  value={formData.st_descricao}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="SKU"
                    name="st_sku"
                    value={formData.st_sku}
                    onChange={handleInputChange}
                    placeholder="Código SKU do produto"
                  />
                  <FormControl fullWidth>
                    <InputLabel>Categoria *</InputLabel>
                    <Select
                      name="categoriaId"
                      value={formData.categoriaId}
                      onChange={handleInputChange}
                      label="Categoria *"
                      error={!!formErrors.categoriaId}
                    >
                      <MenuItem value="">
                        <em>Selecione uma categoria</em>
                      </MenuItem>
                      {categorias.map((cat) => (
                        <MenuItem key={cat.categoriaId} value={cat.categoriaId}>
                          {cat.st_nomeCategoria}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Tipo Item</InputLabel>
                    <Select
                      name="tipo_item"
                      value={formData.tipo_item}
                      onChange={handleInputChange}
                      label="Tipo Item"
                    >
                      <MenuItem value="BEM">Bem</MenuItem>
                      <MenuItem value="SERVICO">Serviço</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>Tipo Preço</InputLabel>
                    <Select
                      name="tipo_preco"
                      value={formData.tipo_preco}
                      onChange={handleInputChange}
                      label="Tipo Preço"
                    >
                      <MenuItem value="FIXO">Fixo</MenuItem>
                      <MenuItem value="SOB_CONSULTA">Sob Consulta</MenuItem>
                      <MenuItem value="VARIAVEL_POR_MEDIDA">Variável por Medida</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.bo_ativo === 1}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          bo_ativo: e.target.checked ? 1 : 0
                        }))}
                        name="bo_ativo"
                      />
                    }
                    label="Produto Ativo"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.bo_kit === 1}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          bo_kit: e.target.checked ? 1 : 0
                        }))}
                        name="bo_kit"
                      />
                    }
                    label="É Kit"
                  />
                </Box>
              </Box>
            )}

            {/* Tab Unidades */}
            {activeTab === 1 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1">Unidades Comerciais do Produto</Typography>
                  <Button size="small" onClick={adicionarUnidade} startIcon={<AddIcon />}>
                    Adicionar Unidade
                  </Button>
                </Box>
                {formData.unidades.map((unidade, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'center', p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <TextField
                      label="Unidade"
                      value={unidade.st_unidade || ''}
                      onChange={(e) => atualizarUnidade(index, 'st_unidade', e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                      placeholder="Ex: metro, kg, unidade"
                      required
                    />
                    <TextField
                      label="Preço"
                      type="number"
                      value={unidade.vl_preco || ''}
                      onChange={(e) => atualizarUnidade(index, 'vl_preco', e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                      placeholder="0.00"
                      inputProps={{ step: "0.01", min: "0" }}
                    />
                    <TextField
                      label="Fator Conversão"
                      type="number"
                      value={unidade.nu_fatorConversao || ''}
                      onChange={(e) => atualizarUnidade(index, 'nu_fatorConversao', e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                      placeholder="1.0000"
                      inputProps={{ step: "0.0001", min: "0" }}
                    />
                    <IconButton onClick={() => removerUnidade(index)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                {formData.unidades.length === 0 && (
                  <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                    Nenhuma unidade cadastrada. Clique em "Adicionar Unidade" para começar.
                  </Typography>
                )}
              </Box>
            )}

            {/* Tab Dimensões */}
            {activeTab === 2 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1">Dimensões do Produto</Typography>
                  <Button size="small" onClick={adicionarDimensao} startIcon={<AddIcon />}>
                    Adicionar Dimensão
                  </Button>
                </Box>
                {formData.dimensoes.map((dimensao, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'center', p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <TextField
                      label="Comprimento (m)"
                      type="number"
                      value={dimensao.nu_comprimento || ''}
                      onChange={(e) => atualizarDimensao(index, 'nu_comprimento', e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="Largura (m)"
                      type="number"
                      value={dimensao.nu_largura || ''}
                      onChange={(e) => atualizarDimensao(index, 'nu_largura', e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="Espessura (m)"
                      type="number"
                      value={dimensao.nu_espessura || ''}
                      onChange={(e) => atualizarDimensao(index, 'nu_espessura', e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                    <IconButton onClick={() => removerDimensao(index)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                {formData.dimensoes.length === 0 && (
                  <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                    Nenhuma dimensão cadastrada. Clique em "Adicionar Dimensão" para começar.
                  </Typography>
                )}
              </Box>
            )}

            {/* Tab Imagens */}
            {activeTab === 3 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1">Imagens do Produto</Typography>
                  <Button size="small" onClick={adicionarImagem} startIcon={<AddIcon />}>
                    Adicionar Imagem
                  </Button>
                </Box>
                {formData.imagens.map((imagem, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'center', p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <TextField
                      fullWidth
                      label="URL da Imagem"
                      value={imagem.st_urlImagem || imagem.st_url || ''}
                      onChange={(e) => atualizarImagem(index, e.target.value)}
                      size="small"
                    />
                    {(imagem.st_urlImagem || imagem.st_url) && (
                      <Avatar src={imagem.st_urlImagem || imagem.st_url} sx={{ width: 56, height: 56 }} />
                    )}
                    <IconButton onClick={() => removerImagem(index)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                {formData.imagens.length === 0 && (
                  <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                    Nenhuma imagem cadastrada. Clique em "Adicionar Imagem" para começar.
                  </Typography>
                )}
              </Box>
            )}

            {/* Tab Atributos */}
            {activeTab === 4 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1">Atributos do Produto</Typography>
                  <Button size="small" onClick={adicionarAtributo} startIcon={<AddIcon />}>
                    Adicionar Atributo
                  </Button>
                </Box>
                {formData.atributos.map((atributo, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'center', p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <TextField
                      label="Nome do Atributo"
                      value={atributo.st_nome || ''}
                      onChange={(e) => atualizarAtributo(index, 'st_nome', e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="Valor"
                      value={atributo.st_valor || ''}
                      onChange={(e) => atualizarAtributo(index, 'st_valor', e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                    <FormControl size="small" sx={{ width: 140 }}>
                      <InputLabel>Tipo</InputLabel>
                      <Select
                        label="Tipo"
                        value={atributo.st_tipo || 'QUANTIDADE'}
                        onChange={(e) => atualizarAtributo(index, 'st_tipo', e.target.value)}
                      >
                        <MenuItem value="QUANTIDADE">Quantidade</MenuItem>
                        <MenuItem value="MONETARIO">Monetário (R$)</MenuItem>
                      </Select>
                    </FormControl>
                    <IconButton onClick={() => removerAtributo(index)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                {formData.atributos.length === 0 && (
                  <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                    Nenhum atributo cadastrado. Clique em "Adicionar Atributo" para começar.
                  </Typography>
                )}
              </Box>
            )}

            {/* Tab Tags */}
            {activeTab === 5 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1">Tags do Produto</Typography>
                  <Button size="small" onClick={adicionarTag} startIcon={<AddIcon />}>
                    Adicionar Tag
                  </Button>
                </Box>
                {formData.tags.map((tag, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'center', p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <TextField
                      fullWidth
                      label="Tag"
                      value={tag.st_tag || ''}
                      onChange={(e) => atualizarTag(index, e.target.value)}
                      size="small"
                    />
                    <IconButton onClick={() => removerTag(index)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                {formData.tags.length === 0 && (
                  <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                    Nenhuma tag cadastrada. Clique em "Adicionar Tag" para começar.
                  </Typography>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            padding: 2,
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderTop: 1,
            borderColor: 'divider',
            zIndex: 1,
            height: '60px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="body2" color="text.secondary">
              {formatarDataHora(dataHoraAtual)}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => setActiveTab(prev => Math.max(0, prev - 1))}
                disabled={activeTab === 0}
              >
                Anterior
              </Button>
              <Button
                variant="outlined"
                onClick={() => setActiveTab(prev => Math.min(5, prev + 1))}
                disabled={activeTab === 5}
              >
                Próximo
              </Button>
              <Button
                variant="outlined"
                onClick={fecharFormulario}
              >
                FECHAR
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={salvarProduto}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {loading ? "SALVANDO..." : "SALVAR"}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Dialog de Visualização */}
        <Dialog
          open={openView}
          onClose={fecharVisualizacao}
          maxWidth={false}
          fullWidth={false}
          disableEscapeKeyDown
          disablePortal
          hideBackdrop={false}
          sx={{
            '& .MuiDialog-backdrop': {
              backgroundColor: 'rgba(0, 0, 0, 0.8)'
            }
          }}
          PaperProps={{
            sx: {
              width: '900px',
              height: '550px',
              position: 'relative'
            }
          }}
        >
          <DialogTitle sx={{
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderBottom: 1,
            borderColor: 'divider',
            padding: 2,
            position: 'sticky',
            top: 0,
            zIndex: 1,
            fontSize: '1.25rem',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            Visualizar Produto
            <IconButton onClick={fecharVisualizacao}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{
            height: 'calc(100% - 140px)',
            overflow: 'auto',
            padding: 2
          }}>
            {produtoSelecionado && (() => {
              const produtoData = produtoSelecionado.produto || produtoSelecionado;
              const unidades = produtoData.unidades || produtoData.prodmad_unidades || [];
              const dimensoes = produtoData.dimensoes || produtoData.prodmad_dimensaos || [];
              const imagens = produtoData.imagens || produtoData.prodmad_imagems || [];
              const atributos = produtoData.atributos || produtoData.prodmad_atributos || [];
              const tags = produtoData.tags || produtoData.prodmad_tags || [];

              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Tabs */}
                  <Tabs value={activeTabView} onChange={(e, newValue) => setActiveTabView(newValue)}>
                    <Tab label="DADOS BÁSICOS" />
                    <Tab label="UNIDADES" />
                    <Tab label="DIMENSÕES" />
                    <Tab label="IMAGENS" />
                    <Tab label="ATRIBUTOS" />
                    <Tab label="TAGS" />
                  </Tabs>

                  {/* Tab Dados Básicos */}
                  {activeTabView === 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        label="Nome do Produto"
                        value={produtoData.st_nomeProduto || ''}
                        fullWidth
                        InputProps={{ readOnly: true }}
                        sx={{
                          '& .MuiInputBase-input': {
                            color: 'rgba(0, 0, 0, 0.6)',
                            WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                          },
                          '& .MuiInputLabel-root': {
                            color: 'rgba(0, 0, 0, 0.6)'
                          }
                        }}
                      />
                      <TextField
                        label="Descrição"
                        value={produtoData.st_descricao || ''}
                        fullWidth
                        multiline
                        rows={3}
                        InputProps={{ readOnly: true }}
                        sx={{
                          '& .MuiInputBase-input': {
                            color: 'rgba(0, 0, 0, 0.6)',
                            WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                          },
                          '& .MuiInputLabel-root': {
                            color: 'rgba(0, 0, 0, 0.6)'
                          }
                        }}
                      />
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                          label="SKU"
                          value={produtoData.st_sku || ''}
                          fullWidth
                          InputProps={{ readOnly: true }}
                          sx={{
                            '& .MuiInputBase-input': {
                              color: 'rgba(0, 0, 0, 0.6)',
                              WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                              backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            },
                            '& .MuiInputLabel-root': {
                              color: 'rgba(0, 0, 0, 0.6)'
                            }
                          }}
                        />
                        <TextField
                          label="Categoria"
                          value={categorias.find(c => c.categoriaId === produtoData.categoriaId)?.st_nomeCategoria || ''}
                          fullWidth
                          InputProps={{ readOnly: true }}
                          sx={{
                            '& .MuiInputBase-input': {
                              color: 'rgba(0, 0, 0, 0.6)',
                              WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                              backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            },
                            '& .MuiInputLabel-root': {
                              color: 'rgba(0, 0, 0, 0.6)'
                            }
                          }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <FormControl fullWidth>
                          <InputLabel>Tipo Item</InputLabel>
                          <Select
                            value={produtoData.tipo_item || 'BEM'}
                            label="Tipo Item"
                            disabled
                            sx={{
                              '& .MuiInputBase-input': {
                                color: 'rgba(0, 0, 0, 0.6)',
                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                              }
                            }}
                          >
                            <MenuItem value="BEM">Bem</MenuItem>
                            <MenuItem value="SERVICO">Serviço</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControl fullWidth>
                          <InputLabel>Tipo Preço</InputLabel>
                          <Select
                            value={produtoData.tipo_preco || 'FIXO'}
                            label="Tipo Preço"
                            disabled
                            sx={{
                              '& .MuiInputBase-input': {
                                color: 'rgba(0, 0, 0, 0.6)',
                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                              }
                            }}
                          >
                            <MenuItem value="FIXO">Fixo</MenuItem>
                            <MenuItem value="SOB_CONSULTA">Sob Consulta</MenuItem>
                            <MenuItem value="VARIAVEL_POR_MEDIDA">Variável por Medida</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>
                  )}

                  {/* Tab Unidades */}
                  {activeTabView === 1 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {unidades.length > 0 ? (
                        unidades.map((unid, index) => (
                          <Box key={index} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <TextField
                                label="Unidade"
                                value={unid.st_unidade || ''}
                                fullWidth
                                InputProps={{ readOnly: true }}
                                sx={{
                                  '& .MuiInputBase-input': {
                                    color: 'rgba(0, 0, 0, 0.6)',
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                  }
                                }}
                              />
                              <TextField
                                label="Preço"
                                value={unid.vl_preco ? formatarMoeda(unid.vl_preco) : '-'}
                                fullWidth
                                InputProps={{ readOnly: true }}
                                sx={{
                                  '& .MuiInputBase-input': {
                                    color: 'rgba(0, 0, 0, 0.6)',
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                  }
                                }}
                              />
                              <TextField
                                label="Fator Conversão"
                                value={unid.nu_fatorConversao || '-'}
                                fullWidth
                                InputProps={{ readOnly: true }}
                                sx={{
                                  '& .MuiInputBase-input': {
                                    color: 'rgba(0, 0, 0, 0.6)',
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                  }
                                }}
                              />
                            </Box>
                          </Box>
                        ))
                      ) : (
                        <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                          Nenhuma unidade cadastrada.
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Tab Dimensões */}
                  {activeTabView === 2 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {dimensoes.length > 0 ? (
                        dimensoes.map((dim, index) => (
                          <Box key={index} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>Dimensão {index + 1}</Typography>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <TextField
                                label="Comprimento"
                                value={dim.nu_comprimento || ''}
                                fullWidth
                                InputProps={{ readOnly: true }}
                                sx={{
                                  '& .MuiInputBase-input': {
                                    color: 'rgba(0, 0, 0, 0.6)',
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                  }
                                }}
                              />
                              <TextField
                                label="Largura"
                                value={dim.nu_largura || ''}
                                fullWidth
                                InputProps={{ readOnly: true }}
                                sx={{
                                  '& .MuiInputBase-input': {
                                    color: 'rgba(0, 0, 0, 0.6)',
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                  }
                                }}
                              />
                              <TextField
                                label="Espessura"
                                value={dim.nu_espessura || ''}
                                fullWidth
                                InputProps={{ readOnly: true }}
                                sx={{
                                  '& .MuiInputBase-input': {
                                    color: 'rgba(0, 0, 0, 0.6)',
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                  }
                                }}
                              />
                            </Box>
                          </Box>
                        ))
                      ) : (
                        <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                          Nenhuma dimensão cadastrada.
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Tab Imagens */}
                  {activeTabView === 3 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {imagens.length > 0 ? (
                        imagens.map((img, index) => (
                          <Box key={index} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                            <TextField
                              label="URL da Imagem"
                              value={img.st_urlImagem || img.st_url || ''}
                              fullWidth
                              InputProps={{ readOnly: true }}
                              sx={{
                                '& .MuiInputBase-input': {
                                  color: 'rgba(0, 0, 0, 0.6)',
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                              }}
                            />
                            {(img.st_urlImagem || img.st_url) && (
                              <Avatar
                                src={img.st_urlImagem || img.st_url}
                                sx={{ width: 100, height: 100, mt: 2 }}
                              />
                            )}
                          </Box>
                        ))
                      ) : (
                        <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                          Nenhuma imagem cadastrada.
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Tab Atributos */}
                  {activeTabView === 4 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {atributos.length > 0 ? (
                        atributos.map((attr, index) => (
                          <Box key={index} sx={{ display: 'flex', gap: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                            <TextField
                              label="Nome do Atributo"
                              value={attr.st_nomeAtributo || attr.st_nome || ''}
                              fullWidth
                              InputProps={{ readOnly: true }}
                              sx={{
                                '& .MuiInputBase-input': {
                                  color: 'rgba(0, 0, 0, 0.6)',
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                              }}
                            />
                            <TextField
                              label="Valor"
                              value={attr.st_valorAtributo || attr.st_valor || ''}
                              fullWidth
                              InputProps={{ readOnly: true }}
                              sx={{
                                '& .MuiInputBase-input': {
                                  color: 'rgba(0, 0, 0, 0.6)',
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                              }}
                            />
                            <TextField
                              label="Tipo"
                              value={attr.st_tipo === 'MONETARIO' ? 'Monetário (R$)' : 'Quantidade'}
                              fullWidth
                              InputProps={{ readOnly: true }}
                              sx={{
                                '& .MuiInputBase-input': {
                                  color: 'rgba(0, 0, 0, 0.6)',
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                              }}
                            />
                          </Box>
                        ))
                      ) : (
                        <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                          Nenhum atributo cadastrado.
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Tab Tags */}
                  {activeTabView === 5 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {tags.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {tags.map((tag, index) => (
                            <Chip
                              key={index}
                              label={tag.st_tag || tag}
                              sx={{
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                color: 'rgba(0, 0, 0, 0.6)'
                              }}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                          Nenhuma tag cadastrada.
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })()}
          </DialogContent>
          <DialogActions sx={{
            padding: 2,
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderTop: 1,
            borderColor: 'divider'
          }}>
            <Button onClick={fecharVisualizacao} variant="contained">
              FECHAR
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de Confirmação de Exclusão */}
        <Dialog
          open={openDeleteConfirm}
          onClose={fecharConfirmacaoExclusao}
        >
          <DialogTitle>Confirmar Exclusão</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Tem certeza que deseja excluir o produto "{produtoParaExcluir ? (produtoParaExcluir.produto || produtoParaExcluir).st_nomeProduto : ''}"?
              Esta ação não pode ser desfeita.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={fecharConfirmacaoExclusao}>Cancelar</Button>
            <Button onClick={deletarProduto} color="error" variant="contained">
              Excluir
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de Cálculo de Preço */}
        <Dialog
          open={openCalculo}
          onClose={() => setOpenCalculo(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalculateIcon />
              Calcular Preço
            </Box>
          </DialogTitle>
          <DialogContent>
            {produtoCalculo && (() => {
              const produtoData = produtoCalculo.produto || produtoCalculo;
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                  <TextField
                    label="Produto"
                    value={produtoData.st_nomeProduto || ''}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={{
                      '& .MuiInputBase-input': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  />
                  <TextField
                    label="Quantidade"
                    type="number"
                    value={quantidadeCalculo}
                    onChange={(e) => setQuantidadeCalculo(parseFloat(e.target.value) || 1)}
                    fullWidth
                    inputProps={{ min: 1, step: 0.01 }}
                  />
                  <Button
                    variant="contained"
                    onClick={calcularPreco}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} /> : <CalculateIcon />}
                    fullWidth
                  >
                    {loading ? 'Calculando...' : 'Calcular'}
                  </Button>
                  {resultadoCalculo && (
                    <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1, mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Resultado do Cálculo
                      </Typography>
                      <Typography variant="body2">
                        <strong>Tipo:</strong> {resultadoCalculo.tipo || '-'}
                      </Typography>
                      {resultadoCalculo.calculo && (
                        <Typography variant="body2">
                          <strong>Medida:</strong> {resultadoCalculo.calculo} {resultadoCalculo.tipo}
                        </Typography>
                      )}
                      <Typography variant="body2">
                        <strong>Preço Unitário:</strong> {resultadoCalculo.preco_unitario && resultadoCalculo.preco_unitario > 0 ? formatarMoeda(resultadoCalculo.preco_unitario) : 'R$ 0,00'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Quantidade:</strong> {resultadoCalculo.quantidade || quantidadeCalculo}
                      </Typography>
                      <Typography variant="h6" sx={{ mt: 1, color: resultadoCalculo.valor_final && resultadoCalculo.valor_final > 0 ? 'primary.main' : 'error.main' }}>
                        <strong>Valor Final:</strong> {resultadoCalculo.valor_final && resultadoCalculo.valor_final > 0 ? formatarMoeda(resultadoCalculo.valor_final) : 'R$ 0,00'}
                      </Typography>
                      {(!resultadoCalculo.preco_unitario || resultadoCalculo.preco_unitario === 0) && (
                        <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(255, 193, 7, 0.1)', borderRadius: 1 }}>
                          <Typography variant="body2" color="warning.main">
                            ⚠️ Atenção: O produto não possui preço cadastrado. Cadastre uma unidade com preço na aba "Unidades" do formulário de edição.
                          </Typography>
                        </Box>
                      )}
                      {resultadoCalculo.mensagem && (
                        <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(255, 193, 7, 0.1)', borderRadius: 1 }}>
                          <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                            {resultadoCalculo.mensagem}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })()}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCalculo(false)}>Fechar</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AcessoRestrito>
  );
}

